/**
 * Header — Laganini.
 * - Mide la altura del header y la expone como --iurop-header-h (para el solape
 *   transparente sobre el hero).
 * - Alterna .header-scrolled en <html> al pasar el hero (o un umbral) para
 *   cambiar de transparente a sólido.
 * - Drawer móvil (abrir/cerrar/overlay/Esc + bloqueo de scroll) y submenús.
 * - Placeholder animado del buscador (efecto máquina de escribir).
 * - Auto-submit de los selects de idioma/moneda.
 * Ver NOTAS-DESARROLLO.md §3.3.
 */
(function () {
  'use strict';

  const root = document.documentElement;

  // ---- Overlays exclusivos del header -------------------------------------
  // Buscador y menú móvil no pueden convivir (sus clases en <html> se pisan y
  // el segundo quedaba inalcanzable tras el primero). Cada uno registra aquí su
  // `close` y abrir uno cierra al otro.
  const headerOverlays = {};
  const closeOtherOverlays = (keep) => {
    Object.keys(headerOverlays).forEach((name) => {
      if (name !== keep && typeof headerOverlays[name] === 'function') {
        headerOverlays[name]();
      }
    });
  };

  // ---- Altura del header + estado scrolled --------------------------------

  const setupScrollState = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    const hero = document.querySelector('[data-hero]');
    const slot = header.querySelector('[data-header-logo]');
    const fly = header.querySelector('[data-flylogo]');

    // El logo volador solo actua en la home (header transparente sobre el hero),
    // con hero presente, el ajuste activo y ambos logos en el DOM.
    const flyEnabled =
      root.classList.contains('header-transparent') &&
      !!hero &&
      header.getAttribute('data-hero-logo') === 'true' &&
      !!slot &&
      !!fly;

    if (flyEnabled) root.classList.add('lg-flylogo-active');

    let threshold = 1;
    // Tween del logo SOLO al cambiar el estado forzado (abrir/cerrar buscador
    // o megamenú): en scroll normal el logo va clavado al frame (sin
    // transición, o iría con lag de goma). `.is-anim` activa la transición
    // CSS durante ~medio segundo alrededor del cambio.
    let flyForcedPrev = false;
    let flyAnimTimer = null;
    // Píxeles que le quedan al logo volador para aterrizar (0 = aterrizado).
    let flyRemaining = 0;
    // Histéresis del estado sólido: sin ella, el rebote del scroll en móvil
    // cruza el umbral varias veces seguidas y el header parpadea.
    const SCROLL_HYST = 24;

    const measure = () => {
      const h = header.offsetHeight;
      if (h) root.style.setProperty('--iurop-header-h', h + 'px');
      if (hero) {
        const announcementHeight = Array.from(document.querySelectorAll('.announcement-bar'))
          .reduce((height, bar) => height + bar.offsetHeight, 0);
        const offset = announcementHeight + (root.classList.contains('header-transparent') ? 0 : h);
        root.style.setProperty('--iurop-hero-top', offset + 'px');
      }
      // "Pasa el hero": scroll > alto del hero menos el del header. Sin hero,
      // umbral mínimo (frost casi al instante). Es también el fin de la
      // animación del logo (aterriza justo cuando el hero se va).
      threshold = hero ? Math.max(hero.offsetHeight - h, 1) : 1;
    };

    // Mueve el logo volador (fixed) entre el ancla del hero (grande) y el slot
    // del header (pequeño).
    //
    // La animación NO se gobierna por scrollY/threshold: el ancla del hero sube
    // con el scroll por su cuenta y, al final del recorrido, ya está POR ENCIMA
    // del header -> interpolar contra ella empujaba el logo fuera del borde
    // superior (se cortaba en móvil). Se gobierna por la DISTANCIA real que
    // queda entre el ancla y su destino, acotada a [0, d0]:
    //   - la posición nunca sube por encima del destino (imposible salirse),
    //   - la escala llega a 1 EXACTAMENTE al aterrizar (van sincronizadas).
    // `forceDocked` (buscador/menú abiertos) fuerza el estado aterrizado.
    // Deja en `flyRemaining` la distancia que le queda al logo (px).
    const positionFly = (forceDocked) => {
      const sr = slot.getBoundingClientRect();
      const hr = hero.getBoundingClientRect();
      const hdr = header.getBoundingClientRect();

      // Geometría degenerada (slot sin ancho, hero sin alto, un NaN suelto):
      // no se inventa una posición — se da por aterrizado y manda el logo
      // normal del header, que siempre está en su sitio.
      if (!sr.width || !hr.height) {
        flyRemaining = 0;
        return;
      }

      const dockX = sr.left + sr.width / 2;
      const dockY = sr.top + sr.height / 2;

      const anchor = 0.5;
      const heroAnchorY = hr.top + hr.height * anchor;

      // d0 = separación ancla↔destino con la página arriba del todo (constante,
      // en coords de documento: el header es sticky, así que su posición de
      // reposo es header.offsetTop = alto de la announcement bar).
      const slotInHeader = sr.top - hdr.top + sr.height / 2;
      const dockY0 = header.offsetTop + slotInHeader;
      const heroTopDoc = hr.top + window.scrollY;
      const d0 = heroTopDoc + hr.height * anchor - dockY0;

      // Distancia que queda (0 = aterrizado, d0 = arriba del todo).
      const usable = isFinite(d0) && d0 > 0;
      const d = usable ? Math.min(Math.max(heroAnchorY - dockY, 0), d0) : 0;
      const r = forceDocked || !usable ? 0 : d / d0;
      // Distancia GEOMÉTRICA (ignora forceDocked): al soltar el estado forzado
      // el relevo tiene que saber si el logo sigue en vuelo.
      flyRemaining = d;

      const targetW = hr.width * 0.92;

      // El logo se MAQUETA al tamaño grande y solo se escala hacia ABAJO al
      // aterrizar. Al revés (maquetar al ancho del slot y escalar x2-x4) el
      // navegador amplía el rasterizado del SVG y el logo sale pixelado.
      // El ancho del slot varía por viewport (198 desktop / 124 móvil), así que
      // la escala de aterrizaje se recalcula en cada pasada.
      const bigW = Math.max(targetW, sr.width);
      const dockScale = sr.width / bigW;

      fly.style.width = bigW.toFixed(1) + 'px';
      fly.style.left = dockX.toFixed(1) + 'px';
      fly.style.top = (dockY + (forceDocked ? 0 : d)).toFixed(1) + 'px';
      fly.style.transform =
        'translate(-50%, -50%) scale(' +
        (dockScale + (1 - dockScale) * r).toFixed(4) +
        ')';
    };

    // Tono del contenido bajo el header (claro/oscuro) → color del logo y la nav.
    // Devuelve null cuando la muestra no dice nada del contenido de la página.
    const toneAt = (y) => {
      const el = document.elementFromPoint(window.innerWidth / 2, y);
      if (!el) return null;
      // Si la muestra cae sobre el PROPIO chrome del header (megamenú o panel
      // de búsqueda abriéndose/cerrándose: siguen visibles durante su fade),
      // no dice nada del contenido de la página -> conservar el tono anterior.
      // Sin este guard, al cerrar el megamenú se muestreaba el panel blanco
      // aún desvaneciéndose y el logo/nav quedaban NEGROS sobre el hero.
      if (el.closest('.iurop-header-section')) return null;
      const toned = el.closest('[data-header-tone]');
      return toned ? toned.getAttribute('data-header-tone') || 'light' : 'light';
    };

    const updateTone = () => {
      // Puntos de muestreo = justo DEBAJO de la franja del header en coordenadas
      // de viewport (getBoundingClientRect, NO offsetHeight: la announcement bar
      // empuja el header hacia abajo, así que el header no empieza en y=0).
      //
      // DOS muestras y solo se cambia el tono si coinciden: en el borde entre
      // dos secciones una de ellas cambia antes que la otra y, scrolleando
      // rápido en móvil, el logo alternaba blanco/negro (parpadeo).
      const bottom = header.getBoundingClientRect().bottom;
      const near = toneAt(bottom + 6);
      const far = toneAt(bottom + 28);
      if (!near || near !== far) return;
      root.classList.toggle('header-on-dark', near === 'dark');
    };

    const apply = () => {
      const y = window.scrollY;
      if (root.classList.contains('header-scrolled')) {
        if (y < Math.max(threshold - SCROLL_HYST, 1)) {
          root.classList.remove('header-scrolled');
        }
      } else if (y >= threshold) {
        root.classList.add('header-scrolled');
      }

      if (flyEnabled) {
        // positionFly espera un BOOLEANO (forceDocked) y saca el progreso de la
        // geometría. Pasarle el progreso numérico hacía que cualquier valor > 0
        // se leyera como "aterrizado" y el logo saltaba al header al primer px.
        const forced =
          root.classList.contains('iurop-search-open') ||
          root.classList.contains('iurop-menu-open') ||
          root.classList.contains('iurop-mega-open');
        if (forced !== flyForcedPrev) {
          flyForcedPrev = forced;
          fly.classList.add('is-anim');
          window.clearTimeout(flyAnimTimer);
          flyAnimTimer = window.setTimeout(() => {
            fly.classList.remove('is-anim');
            // Terminado el tween se re-evalúa el relevo (puede tocar aterrizar).
            apply();
          }, 500);
        }
        positionFly(forced);

        // Relevo: en cuanto el vuelo termina manda el logo NORMAL del header
        // (en flujo). El volador es `position:fixed` y su sitio depende de la
        // geometría del hero, que lejos del hero deja de ser fiable — de ahí
        // que "al bajar hasta el footer" el logo acabara fuera de sitio. En el
        // punto de relevo los dos pintan igual (misma posición y escala), así
        // que el cambio no se ve, y deja de reposicionarse un elemento fixed en
        // cada evento de scroll (origen del parpadeo en móvil).
        //
        // El salvavidas por umbral cubre un hero que no se mueva con el scroll
        // (`stack_under`): ahí la distancia se congela y no aterrizaría nunca.
        const landed =
          !forced &&
          !fly.classList.contains('is-anim') &&
          (flyRemaining <= 1 || y >= threshold + SCROLL_HYST);
        root.classList.toggle('lg-flylogo-active', !landed);
      }

      updateTone();
    };

    // Sincrono a proposito (sin guard de requestAnimationFrame): si la pestaña
    // esta en segundo plano el rAF NO dispara, el flag se queda levantado y el
    // header/logo se congelan para siempre. El trabajo es un puñado de
    // getBoundingClientRect + escrituras de estilo: barato para el scroll.
    const onScroll = apply;

    measure();
    apply();

    document.addEventListener('laganini:hero-mode-change', apply);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => {
      measure();
      apply();
    });

    // Recalcula cuando la imagen del hero termina de cargar (cambia su alto).
    window.addEventListener('load', () => {
      measure();
      apply();
    });

    if ('ResizeObserver' in window) {
      const headerResizeObserver = new ResizeObserver(() => {
        measure();
        apply();
      });
      headerResizeObserver.observe(header);
      document.querySelectorAll('.announcement-bar').forEach((bar) => headerResizeObserver.observe(bar));
    }
  };

  // ---- Drawer móvil (acordeones, Figma 13772:429) -------------------------

  const setupDrawer = () => {
    const drawer = document.querySelector('[data-header-drawer]');
    const burger = document.querySelector('[data-header-burger]');
    if (!drawer || !burger) return;

    // Ancla el drawer justo BAJO la fila del header. Se recalcula al abrir y en
    // scroll/resize: si se calcula una sola vez (o antes de que asiente el
    // layout) el panel acaba tapando la propia fila del header (Figma la
    // mantiene visible con la X, el logo y los iconos).
    const placeDrawer = () => {
      const bar = document.querySelector('.iurop-header__bar');
      if (!bar) return;
      drawer.style.top = Math.max(bar.getBoundingClientRect().bottom, 0) + 'px';
    };

    const open = () => {
      closeOtherOverlays('menu');
      placeDrawer();
      // 2o pase tras el reflow (fuentes/sticky pueden mover la barra)
      window.requestAnimationFrame(placeDrawer);
      window.addEventListener('scroll', placeDrawer, { passive: true });
      window.addEventListener('resize', placeDrawer);
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      burger.setAttribute('aria-expanded', 'true');
      root.setAttribute('data-drawer-locked', 'true');
      root.classList.add('iurop-menu-open');
      window.dispatchEvent(new Event('scroll')); // re-ancla el logo volador
    };

    const close = () => {
      window.removeEventListener('scroll', placeDrawer);
      window.removeEventListener('resize', placeDrawer);
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      burger.setAttribute('aria-expanded', 'false');
      root.removeAttribute('data-drawer-locked');
      root.classList.remove('iurop-menu-open');
      window.dispatchEvent(new Event('scroll'));
    };

    headerOverlays.menu = close;

    // El burger alterna: abierto -> X cierra (Figma: la barra muestra X).
    burger.addEventListener('click', () => {
      if (drawer.classList.contains('is-open')) {
        close();
      } else {
        open();
      }
    });

    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-header-drawer-close]')) close();
      // Navegar desde un enlace del drawer también lo cierra.
      if (e.target.closest('a[href]')) close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // Acordeones: cada item con hijos despliega su lista debajo.
    drawer.querySelectorAll('[data-drawer-accordion]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const pane = btn.nextElementSibling;
        if (!pane) return;
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        if (!expanded) {
          drawer.querySelectorAll('[data-drawer-accordion].is-open').forEach((otherBtn) => {
            if (otherBtn === btn) return;
            const otherPane = otherBtn.nextElementSibling;
            otherBtn.setAttribute('aria-expanded', 'false');
            otherBtn.classList.remove('is-open');
            if (otherPane) otherPane.hidden = true;
          });
        }
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        btn.classList.toggle('is-open', !expanded);
        pane.hidden = expanded;
      });
    });
  };

  // ---- Auto-submit de idioma / moneda -------------------------------------

  const setupLocale = () => {
    document.querySelectorAll('[data-locale-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (form) form.submit();
      });
    });
  };

  // ---- Placeholder animado del buscador -----------------------------------

  const setupSearchAnimation = () => {
    const inputs = document.querySelectorAll('[data-search-animated]');
    if (!inputs.length) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    inputs.forEach((input) => {
      const raw = (input.dataset.suggestions || '').trim();
      const phrases = raw ? raw.split('|').map((s) => s.trim()).filter(Boolean) : [];
      if (phrases.length === 0 || prefersReducedMotion) return;

      const base = input.getAttribute('placeholder') || '';
      let phraseIndex = 0;
      let charIndex = 0;
      let deleting = false;
      let timer = null;
      let paused = false;

      const TYPE = 70;
      const ERASE = 40;
      const HOLD = 1600;

      const step = () => {
        if (paused) return;
        const phrase = phrases[phraseIndex];
        if (!deleting) {
          charIndex++;
          input.setAttribute('placeholder', phrase.slice(0, charIndex));
          if (charIndex >= phrase.length) {
            deleting = true;
            timer = window.setTimeout(step, HOLD);
            return;
          }
          timer = window.setTimeout(step, TYPE);
        } else {
          charIndex--;
          input.setAttribute('placeholder', phrase.slice(0, charIndex));
          if (charIndex <= 0) {
            deleting = false;
            phraseIndex = (phraseIndex + 1) % phrases.length;
            timer = window.setTimeout(step, TYPE);
            return;
          }
          timer = window.setTimeout(step, ERASE);
        }
      };

      const start = () => {
        paused = false;
        window.clearTimeout(timer);
        timer = window.setTimeout(step, 800);
      };
      const stop = () => {
        paused = true;
        window.clearTimeout(timer);
      };

      // Pausa la animación mientras el usuario interactúa con el campo.
      input.addEventListener('focus', () => {
        stop();
        input.setAttribute('placeholder', base);
      });
      input.addEventListener('blur', () => {
        if (!input.value) start();
      });

      start();
    });
  };

  // ---- Megamenú desktop (click-to-pin) ------------------------------------
  // Hover abre el panel (CSS); click "fija" el panel para poder moverte a él sin
  // que se cierre. Segundo click en el mismo trigger navega. Click fuera / Esc cierra.

  const setupDesktopMega = () => {
    const triggers = document.querySelectorAll('[data-mega-trigger]');
    if (!triggers.length) return;

    // Con un megamenu abierto (hover o pin) el header se comporta como con el
    // buscador: clase de estado en <html> -> blanco solido + logo aterrizado.
    // El evento scroll re-posiciona el logo volador (mismo truco del drawer).
    //
    // La APERTURA por hover va con clase `.is-open` + temporizador de GRACIA
    // de 160ms al salir (re-entrar lo cancela), no con `:hover` puro: entre el
    // item y el panel el hit-test puede soltar un mouseleave fantasma (hueco
    // fisico o salto del puntero) y el panel parpadeaba/cerraba (2026-07-30).
    const items = Array.prototype.slice.call(
      document.querySelectorAll('[data-mega-item]')
    );
    let closeTimer = null;
    let pinned = null;
    const isSearchOpen = () => root.classList.contains('iurop-search-open');

    const syncRoot = () => {
      const open = !!document.querySelector(
        '[data-mega-item].is-open, [data-mega-item].is-pinned'
      );
      if (root.classList.contains('iurop-mega-open') !== open) {
        root.classList.toggle('iurop-mega-open', open);
        window.dispatchEvent(new Event('scroll'));
        // Al cerrar, re-muestrear el tono cuando el panel YA no está (la
        // transición dura 0.35s y updateTone ignora muestras sobre el panel).
        if (!open) {
          window.setTimeout(() => window.dispatchEvent(new Event('scroll')), 420);
        }
      }
    };
    const openItem = (item) => {
      if (isSearchOpen()) return;
      window.clearTimeout(closeTimer);
      closeTimer = null;
      items.forEach((i) => {
        if (i !== item) i.classList.remove('is-open');
      });
      item.classList.add('is-open');
      syncRoot();
    };
    const scheduleClose = () => {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        closeTimer = null;
        items.forEach((i) => i.classList.remove('is-open'));
        syncRoot();
      }, 160);
    };
    items.forEach((item) => {
      item.addEventListener('mouseenter', () => openItem(item));
      item.addEventListener('mouseleave', scheduleClose);
    });

    // Reconciliador: los eventos enter/leave PIERDEN pasos si el puntero salta
    // (hit-test con leave fantasma y enter que no llega) y el estado queda a
    // medias (panel visible por :hover con el header sin su clase, o clase
    // colgada sin hover). Cada 200ms se sincroniza con la VERDAD del :hover
    // computado; se auto-repara en ambas direcciones. Trabajo: un
    // querySelector — despreciable.
    window.setInterval(() => {
      if (isSearchOpen()) {
        if (document.querySelector('[data-mega-item].is-open, [data-mega-item].is-pinned')) {
          closeAll();
        }
        return;
      }
      const hovered = document.querySelector('[data-mega-item]:hover');
      if (hovered) {
        if (!hovered.classList.contains('is-open')) openItem(hovered);
        return;
      }
      if (document.querySelector('[data-mega-item].is-pinned')) return;
      if (closeTimer === null && document.querySelector('[data-mega-item].is-open')) {
        scheduleClose();
      }
    }, 200);

    const closePinned = () => {
      if (!pinned) return;
      pinned.classList.remove('is-pinned');
      const t = pinned.querySelector('[data-mega-trigger]');
      if (t) t.setAttribute('aria-expanded', 'false');
      pinned = null;
      syncRoot();
    };
    const pin = (item) => {
      closePinned();
      item.classList.add('is-pinned');
      const t = item.querySelector('[data-mega-trigger]');
      if (t) t.setAttribute('aria-expanded', 'true');
      pinned = item;
      syncRoot();
    };

    const closeAll = () => {
      window.clearTimeout(closeTimer);
      closeTimer = null;
      items.forEach((item) => {
        item.classList.remove('is-open', 'is-pinned');
        const trigger = item.querySelector('[data-mega-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
      pinned = null;
      syncRoot();
    };
    headerOverlays.mega = closeAll;

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        if (isSearchOpen()) return;
        const item = trigger.closest('.iurop-header__menu-item');
        if (!item) return;
        if (pinned === item) {
          // Segundo click: deja navegar al enlace de la categoría.
          closePinned();
          return;
        }
        e.preventDefault();
        pin(item);
      });
    });

    document.addEventListener('click', (e) => {
      if (pinned && !pinned.contains(e.target)) closePinned();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closePinned();
    });
  };

  // ---- Quick-add (AJAX) de las product cards ------------------------------

  const updateCartCount = async () => {
    try {
      const base = (window.routes && window.routes.cart_url) || '/cart';
      const res = await fetch(base + '.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('is-hidden', cart.item_count === 0);
      });
    } catch (_) {
      /* noop */
    }
  };

  let quickAddBound = false;
  const setupQuickAdd = () => {
    if (quickAddBound) return; // delegado global: una sola vez (evita añadir 2x)
    quickAddBound = true;
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-quick-add-form]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('[data-quick-add]');
      const id = form.querySelector('[name="id"]');
      if (!id || (btn && btn.classList.contains('is-loading'))) return;
      const previousLabel = btn ? btn.textContent : '';
      const addingLabel = btn && btn.getAttribute('data-label-adding');
      const addedLabel = btn && btn.getAttribute('data-label-added');
      if (btn) {
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
        if (addingLabel) btn.textContent = addingLabel;
      }
      try {
        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const res = await fetch(addUrl.replace(/\.js$/, '') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: id.value, quantity: 1 }], sections: 'iurop-cart-drawer', sections_url: window.location.pathname })
        });
        if (btn) {
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
        }
        if (res.ok) {
          if (btn) {
            btn.classList.add('is-added');
            if (addedLabel) btn.textContent = addedLabel;
            window.setTimeout(() => {
              btn.classList.remove('is-added');
              if (btn.isConnected) btn.textContent = previousLabel;
            }, 1500);
          }
          const added = await res.json();
          // The add response already contains the updated drawer and item count.
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'quick-add', sections: added.sections } }));
          if (!document.querySelector('[data-cart-drawer]') || !added.sections || !added.sections['iurop-cart-drawer']) updateCartCount();
        } else if (btn) {
          btn.textContent = previousLabel;
        }
      } catch (_) {
        if (btn) {
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
          btn.textContent = previousLabel;
        }
      }
    });
  };

  // No enviar una búsqueda vacía → evita caer en una PLP vacía "Busca
  // productos" (cliente 2026-06-16). Cubre el buscador del header y el del
  // panel (ambos role="search"). Con query vacía cancela y reenfoca.
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[role="search"]');
    if (!form) return;
    const input = form.querySelector('input[name="q"]');
    if (input && input.value.trim() === '') {
      e.preventDefault();
      input.value = '';
      input.focus();
    }
  });


  // ------------------------------------------------------------------
  // Panel de búsqueda (Figma "Buscador"): se abre al enfocar el buscador
  // del header (desktop) o al tocar la lupa (móvil). Estado en <html>
  // (.iurop-search-open): oculta el buscador del header, fuerza header
  // sólido sobre transparente y oculta el strip móvil. Cierra con X,
  // Esc o click fuera.
  // ------------------------------------------------------------------
  const setupSearchPanel = () => {
    const panel = document.querySelector('[data-search-panel]');
    if (!panel) return;
    const html = document.documentElement;
    const panelInput = panel.querySelector('[data-search-panel-input]');
    const headerInput = document.querySelector('.iurop-header__search-input');
    const mobileIcon = document.querySelector('[data-search-open]');

    const open = () => {
      if (panel.classList.contains('is-open')) return;
      // Cerrar el menú móvil ANTES de medir: su `data-drawer-locked` bloquea el
      // scroll y su panel tapa la barra que se usa como ancla.
      closeOtherOverlays('search');
      // En movil el panel es fixed a pantalla: anclarlo bajo la barra real
      // (la announcement bar puede estar visible segun el scroll).
      if (window.matchMedia('(max-width: 989px)').matches) {
        const bar = document.querySelector('.iurop-header__bar');
        if (bar) panel.style.top = Math.max(bar.getBoundingClientRect().bottom, 0) + 'px';
      } else {
        panel.style.removeProperty('top');
      }
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      html.classList.add('iurop-search-open');
      window.dispatchEvent(new Event('scroll')); // re-ancla el logo volador
      if (headerInput && panelInput && headerInput.value) panelInput.value = headerInput.value;
      window.setTimeout(() => {
        if (panelInput) panelInput.focus({ preventScroll: true });
      }, 80);
    };

    const close = () => {
      if (!panel.classList.contains('is-open')) return;
      window.clearTimeout(liveTimer);
      invalidateLive();
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      html.classList.remove('iurop-search-open');
      window.dispatchEvent(new Event('scroll'));
    };

    headerOverlays.search = close;

    if (headerInput) {
      headerInput.addEventListener('focus', open);
      headerInput.addEventListener('click', open);
    }
    if (mobileIcon) {
      mobileIcon.addEventListener('click', (e) => {
        e.preventDefault();
        open();
      });
    }
    panel.querySelectorAll('[data-search-close]').forEach((btn) => btn.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
    // ----- Búsqueda en tiempo real (Predictive Search + Section Rendering) ----
    // Al teclear (debounce 150ms, mín. 3 caracteres) pide /search/suggest con
    // section_id=iurop-predictive-search y muestra las product cards
    // renderizadas en servidor sustituyendo la columna de destacados.
    const live = panel.querySelector('[data-search-live]');
    const featured = panel.querySelector('[data-search-featured]');
    const popular = panel.querySelector('.iurop-search__popular');
    let liveTimer = null;
    let liveRequest = 0;
    let liveAbort = null;
    const MIN_LIVE_QUERY_LENGTH = 3;

    const invalidateLive = () => {
      liveRequest += 1;
      if (liveAbort) {
        liveAbort.abort();
        liveAbort = null;
      }
    };

    const resetLive = () => {
      if (!live) return;
      live.hidden = true;
      live.innerHTML = '';
      if (featured) featured.hidden = false;
      if (popular) popular.hidden = false;
    };

    const renderLive = async (term, request) => {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url =
        root +
        'search/suggest?q=' +
        encodeURIComponent(term) +
        // collection: permite mostrar los productos de una colección cuando el
        // término es el nombre de una categoría (ver iurop-predictive-search).
        '&resources[type]=product,collection&resources[limit]=4&resources[options][unavailable_products]=last' +
        '&section_id=iurop-predictive-search';
      try {
        liveAbort = new AbortController();
        const res = await fetch(url, {
          headers: { Accept: 'text/html' },
          signal: liveAbort.signal
        });
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        let results = doc.querySelector('[data-predictive-results]');
        // La API predictiva busca en menos campos que la pagina de resultados.
        // Si no encuentra nada, pedir la busqueda completa para que terminos
        // como "set" muestren aqui los mismos productos que al pulsar Intro.
        // El primer estado vacio nunca se inyecta, evitando el parpadeo de
        // "Sin resultados" antes de que lleguen las cards de respaldo.
        if (results && results.querySelector('[data-predictive-empty]')) {
          const fallbackUrl =
            root +
            'search?q=' +
            encodeURIComponent(term) +
            '&type=product&section_id=iurop-predictive-search';
          const fallbackRes = await fetch(fallbackUrl, {
            headers: { Accept: 'text/html' },
            signal: liveAbort.signal
          });
          if (fallbackRes.ok) {
            const fallbackDoc = new DOMParser().parseFromString(await fallbackRes.text(), 'text/html');
            const fallbackResults = fallbackDoc.querySelector('[data-predictive-results]');
            if (fallbackResults) results = fallbackResults;
          }
        }
        // Descarta respuestas obsoletas (el usuario siguió tecleando)
        if (request !== liveRequest || !panelInput || panelInput.value.trim() !== term) return;
        if (!results || !live) return;
        live.innerHTML = '';
        live.appendChild(results);
        live.hidden = false;
        if (featured) featured.hidden = true;
        if (popular) popular.hidden = true;
        // Re-inicializa las cards inyectadas (galería, hint…)
        document.dispatchEvent(new CustomEvent('iurop:product-list:rendered'));
      } catch (_) {
        /* sin red: se queda el contenido por defecto */
      } finally {
        if (request === liveRequest) liveAbort = null;
      }
    };

    if (panelInput && live) {
      panelInput.addEventListener('input', () => {
        const term = panelInput.value.trim();
        window.clearTimeout(liveTimer);
        invalidateLive();
        if (term.length < MIN_LIVE_QUERY_LENGTH) {
          resetLive();
          return;
        }
        const request = liveRequest;
        liveTimer = window.setTimeout(() => renderLive(term, request), 150);
      });
    }

    const searchForm = panel.querySelector('form[role="search"]');
    if (searchForm) {
      searchForm.addEventListener('submit', () => {
        window.clearTimeout(liveTimer);
        invalidateLive();
      });
    }

    // Cerrar al interactuar fuera. OJO: pointerdown, NO click — al abrir, el
    // CSS esconde el form del header y el click del propio gesto de apertura
    // se re-dirige al fondo del header → un listener de click lo tomaría por
    // "fuera" y cerraría el panel al instante.
    document.addEventListener('pointerdown', (e) => {
      if (!panel.classList.contains('is-open')) return;
      if (e.target.closest('[data-search-panel]')) return;
      if (e.target.closest('.iurop-header__search')) return;
      if (e.target.closest('[data-search-open]')) return;
      // La hamburguesa intercambia buscador y menú en el mismo toque. Si se
      // cerrase aquí, el reflow entre pointerdown y click perdería ese click.
      if (e.target.closest('[data-header-burger]')) return;
      close();
    });
  };

  // ---- Header compacto: hamburguesa cuando el nav no cabe -----------------
  // Por encima del breakpoint tablet, si el menu (o el bloque derecho) no cabe
  // sin pisar el logo, se pasa al layout compacto (burger + lupa) via la clase
  // lg-header-compact en <html>. Medido, no por breakpoint: el menu lo edita
  // el cliente y su ancho cambia con items, idioma o fuente.
  const setupHeaderFit = () => {
    const bar = document.querySelector('.iurop-header__bar');
    const nav = document.querySelector('.iurop-header__nav');
    const logo = document.querySelector('[data-header-logo]');
    const right = document.querySelector('.iurop-header__zone--right');
    if (!bar || !nav || !logo) return;

    // Colision EXACTA por rects, no formula con el lado mas ancho: la grid
    // 1fr/auto/1fr reparte simetrico, pero estimar por anchos disparaba el
    // compacto en un MacBook (1280) donde el nav SI cabe. Solo compacta si el
    // nav (o el bloque derecho) queda a menos de AIR px del logo de verdad.
    const AIR = 12;
    const HYST = 24; // al crecer, re-proba la expansion solo pasado este extra
    let compactAtW = 0; // ancho de barra en el que hubo que compactar

    const collides = () => {
      const l = logo.getBoundingClientRect();
      if (!l.width) return false;
      const n = nav.getBoundingClientRect();
      if (n.width && n.right > l.left - AIR) return true;
      if (right) {
        // OJO: la zona derecha es una columna 1fr de la grid — su rect SIEMPRE
        // linda con el logo. Lo que importa es dónde empieza su CONTENIDO
        // (alineado a la derecha): el borde izquierdo del primer hijo visible.
        let contentLeft = Infinity;
        for (const c of right.children) {
          const r = c.getBoundingClientRect();
          if (r.width) contentLeft = Math.min(contentLeft, r.left);
        }
        if (contentLeft !== Infinity && contentLeft < l.right + AIR) return true;
      }
      return false;
    };

    const fit = () => {
      // Bajo el breakpoint tablet manda la media query de siempre.
      if (window.matchMedia('(max-width: 989px)').matches) {
        root.classList.remove('lg-header-compact');
        return;
      }
      const w = bar.clientWidth;
      if (root.classList.contains('lg-header-compact')) {
        if (w > compactAtW + HYST) {
          // Probar la expansion: quitar la clase, medir en sincrono (antes
          // del paint, sin parpadeo) y revertir si sigue chocando.
          root.classList.remove('lg-header-compact');
          if (collides()) {
            root.classList.add('lg-header-compact');
            compactAtW = w;
          }
        }
        return;
      }
      if (collides()) {
        root.classList.add('lg-header-compact');
        compactAtW = w;
      }
    };

    fit();
    // Debounce con setTimeout, no rAF (rAF no dispara en pestana oculta).
    let t = null;
    window.addEventListener('resize', () => {
      window.clearTimeout(t);
      t = window.setTimeout(fit, 80);
    });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fit).catch(() => {});
    }
  };

  // ---- Contador de la wishlist (corazon del header) -----------------------
  // Fuente de verdad: localStorage 'laganini:wishlist' (lo escribe
  // iurop-product-card.js / iurop-product.js). Se sincroniza al cargar, con
  // el evento wishlist:changed y con 'storage' (otras pestanas).
  const setupWishlistCount = () => {
    const badges = document.querySelectorAll('[data-wishlist-count]');
    if (!badges.length) return;
    const KEY = 'laganini:wishlist';
    const update = () => {
      let n = 0;
      try {
        const raw = JSON.parse(window.localStorage.getItem(KEY) || '[]');
        if (Array.isArray(raw)) n = raw.length;
      } catch (_) {
        /* storage bloqueado: badge oculto */
      }
      badges.forEach((b) => {
        b.textContent = n;
        b.classList.remove('is-hidden');
      });
    };
    update();
    document.addEventListener('wishlist:changed', update);
    window.addEventListener('storage', (e) => {
      if (!e.key || e.key === KEY) update();
    });
  };

  const boot = () => {
    setupWishlistCount();
    setupScrollState();
    setupDrawer();
    setupDesktopMega();
    setupQuickAdd();
    setupLocale();
    setupSearchPanel();
    setupSearchAnimation();
    setupHeaderFit();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init en el editor de temas.
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-header]')) boot();
    else if (e.target.querySelector('.announcement-bar')) window.dispatchEvent(new Event('resize'));
  });
})();
